import { Loading } from "@/components/Loading/SpinerLoading"
import { APP_CONFIG } from "@/config"
import { getEventUrl, getImageUrl } from "@/data/helpers"
import { EventFiltersInput, InputMaybe, useEventsQuery } from "@/gql/generated-types"
import { Carousel } from "@mantine/carousel"
import { Divider, Grid, MantineTheme, Text, createStyles } from "@mantine/core"
import dayjs from "dayjs"
import { isEmpty } from "lodash"
import { useTranslation } from "next-i18next"
import Link from "next/link"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"

const useStyles = createStyles((theme: MantineTheme) => ({
  carouselBlog: {
    // maxWidth: "100%",
    padding: 0,
    margin: 0,
    marginBottom: 60,
    "& > .mantine-Carousel-controls": {
      "& > button": {
        border: "none",
        marginLeft: 0,
        borderRadius: 999,
        width: 38,
        height: 38,
        opacity: 0.3,
        backgroundColor: "#000000",

        "& > svg": {
          color: "#FFFFFF",
          width: "2rem",
          height: "1.5rem",
        },
        display: "none",
      },
      "& > button:nth-child(2)": {
        marginRight: 0,
      },
      [theme.fn.smallerThan("sm")]: {
        display: "none",
      },
    },
  },
  slide: {
    height: 369,
    width: "100%",
    cursor: "pointer",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "8px 0 0 8px",
    },
    [theme.fn.smallerThan("sm")]: {
      height: "auto",
      "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: 8,
      },
    },
  },
  body: {
    margin: 0,
    padding: "32px 48px",
    backgroundColor: "#F8F9FA",
    [theme.fn.smallerThan("sm")]: {
      padding: "1rem",
      "& div:nth-child(2)": {
        fontSize: 14,
        marginTop: 3,
      },
      "& div:nth-child(3)": {
        fontSize: 24,
        marginTop: 3,
      },
      "& div:nth-child(4)": {
        fontSize: 16,
        marginTop: 3,
      },
      "& div:nth-child(5), div:nth-child(6)": {
        display: "none",
      },
      "& div:nth-child(7)": {
        marginTop: 10,
        fontSize: 12,
      },
    },
  },
  title: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  viewAllLink: {
    fontWeight: 400,
    fontSize: "1rem",
  },
}))

const currentTime = new Date().toISOString()

export const CarouselEvent = ({ isAll = false, hasViewAll = true }: { isAll?: boolean; hasViewAll?: boolean }) => {
  const { classes } = useStyles()
  const [filters, setFilters] = useState<InputMaybe<EventFiltersInput> | undefined>({
    date: { gte: currentTime },
    websites: { domain: { eq: APP_CONFIG.WEBSITE } },
  })

  const { locale, ...router } = useRouter()
  const { data, loading } = useEventsQuery({
    variables: {
      locale,
      filters,
    },
    fetchPolicy: "network-only",
  })
  const { t } = useTranslation("common")

  useEffect(() => {
    if (isAll) {
      setFilters({
        websites: { domain: { eq: APP_CONFIG.WEBSITE } },
      })
    }
  }, [isAll])

  // if (!loading && data?.events?.data.length == 0) return null

  return (
    <>
      <Text fz={24} fw={800} mt={30} mb={20} className={classes.title}>
        {t("UPCOMING EVENTS", "UPCOMING EVENTS")}
        {hasViewAll && (
          <Link href={"/events"} className={classes.viewAllLink}>
            {t("View all", "View all")}
          </Link>
        )}
      </Text>

      <Carousel
        className={classes.carouselBlog}
        loop
        withIndicators
        styles={theme => ({
          indicators: {
            bottom: "-1.5rem",
            gap: "1.25rem",
          },
          indicator: {
            backgroundColor: "#999",
            [`&[data-active]`]: {
              width: "2.5rem",
              backgroundColor: theme.colors[theme.primaryColor][6],
            },
          },
        })}
      >
        <Loading isLoading={loading} isEmpty={isEmpty(data?.events?.data)} />
        {data?.events?.data.map((e, i) => (
          <Carousel.Slide
            key={e.id}
            className={classes.slide}
            onClick={() => {
              router.push(getEventUrl(e.attributes?.name, e.id!))
            }}
          >
            <Grid p={0} m={0} h={"100%"} columns={12}>
              <Grid.Col m={0} p={0} span={12} sm={6} h={{ base: "auto", sm: "100%" }}>
                <img
                  src={
                    getImageUrl(
                      e.attributes?.cover.data?.attributes?.url ?? "",
                      e.attributes?.cover.data?.attributes?.provider,
                    ) ?? ""
                  }
                  alt="Cover photo"
                />
              </Grid.Col>
              <Grid.Col className={classes.body} span={12} sm={6} h={{ base: "auto", sm: "100%" }}>
                <Text fw={700} fz={{ base: 12, sm: 14 }} c={"#1AAF4D"}>
                  {t("EVENT", "EVENT")}
                </Text>
                <Text fw={400} fz={16} mt={14} c={"#242f3e"} transform="capitalize" opacity={0.7}>
                  {dayjs(new Date(e.attributes?.date ?? "")).format("MMM, YYYY")}
                </Text>
                <Text fw={800} lh={1} fz={44} mt={6} c={"#242F3E"} transform="uppercase" opacity={0.7}>
                  {dayjs(new Date(e.attributes?.date ?? "")).format("DD")}
                </Text>
                <Text fw={700} fz={18} mt={10} transform="uppercase">
                  {e.attributes?.name}
                </Text>
                <Divider my={"sm"} mt={24} w={"95%"} />
                <Text fz={16} fw={400} mt={"1rem"} lh={1.4}>
                  {e.attributes?.shortDescription}
                </Text>
                <Text fz={16} fw={700} mt={4}>
                  {t("Read more", "Read more")}
                </Text>
              </Grid.Col>
            </Grid>
          </Carousel.Slide>
        ))}
      </Carousel>
    </>
  )
}
